import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type ComponentRef,
  type CSSProperties,
  type Ref,
} from 'react'
import { Slot } from '@radix-ui/react-slot'

import cn from '@components/utils/cn'
import { useIsMobile } from '@components/utils/useIsMobile'
import { Input } from '@components/atoms/Input'
import { Separator } from '@components/atoms/Separator'
import { Sheet, SheetContent } from '@components/atoms/Sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'

const SIDEBAR_COOKIE_NAME = 'sidebar_state'
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = '16.25rem'
const SIDEBAR_WIDTH_MOBILE = '100vw'
const SIDEBAR_WIDTH_ICON = '5rem'
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'

type SidebarContextProps = {
  state: 'expanded' | 'collapsed'
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
}

const SidebarContext = createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.')
  }

  return context
}

const SidebarProvider = ({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ref,
  ...props
}: ComponentProps<'div'> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  ref?: Ref<HTMLDivElement>
}) => {
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = useState(false)

  const [_open, _setOpen] = useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  const toggleSidebar = useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open)
  }, [isMobile, setOpen, setOpenMobile])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  const state = open ? 'expanded' : 'collapsed'

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as CSSProperties
          }
          className={cn(
            'tw:group/sidebar-wrapper tw:flex tw:min-h-svh tw:w-full tw:has-data-[variant=inset]:bg-sidebar',
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  )
}

const Sidebar = ({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ref,
  ...props
}: ComponentProps<'div'> & {
  side?: 'left' | 'right'
  variant?: 'sidebar' | 'floating' | 'inset'
  collapsible?: 'offcanvas' | 'icon' | 'none'
  ref?: Ref<HTMLDivElement>
}) => {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === 'none') {
    return (
      <div
        className={cn(
          'tw:flex tw:h-full tw:w-(--sidebar-width) tw:flex-col tw:bg-sidebar tw:text-sidebar-foreground',
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          side="right"
          aria-label="Sidebar navigation"
          aria-describedby={undefined}
          data-sidebar="sidebar"
          data-mobile="true"
          bgClassName="tw:bg-sidebar"
          className="tw:w-(--sidebar-width) tw:p-0 tw:text-sidebar-foreground tw:[&>button]:hidden"
          style={{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE } as CSSProperties}
        >
          <div className="tw:flex tw:h-full tw:w-full tw:flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      ref={ref}
      className="tw:group tw:peer tw:hidden tw:text-sidebar-foreground tw:lg:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
    >
      <div
        className={cn(
          'tw:relative tw:w-(--sidebar-width) tw:bg-transparent tw:transition-[width] tw:duration-200 tw:ease-linear',
          'tw:group-data-[collapsible=offcanvas]:w-0',
          'tw:group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'tw:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+1rem)]'
            : 'tw:group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
      />
      <div
        className={cn(
          'tw:fixed tw:bottom-0 tw:top-(--sb-banner-height,0px) tw:z-10 tw:hidden tw:w-(--sidebar-width) tw:transition-[left,right,width] tw:duration-200 tw:ease-linear tw:lg:flex',
          side === 'left'
            ? 'tw:left-0 tw:group-data-[collapsible=offcanvas]:-left-(--sidebar-width)'
            : 'tw:right-0 tw:group-data-[collapsible=offcanvas]:-right-(--sidebar-width)',
          variant === 'floating' || variant === 'inset'
            ? 'tw:p-2 tw:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+1rem+2px)]'
            : 'tw:group-data-[collapsible=icon]:w-(--sidebar-width-icon) tw:group-data-[side=left]:border-r tw:group-data-[side=right]:border-l',
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className="tw:flex tw:h-full tw:w-full tw:flex-col tw:bg-sidebar tw:group-data-[variant=floating]:rounded-lg tw:group-data-[variant=floating]:border tw:group-data-[variant=floating]:border-sidebar-border tw:group-data-[variant=floating]:shadow"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

const SidebarTrigger = ({
  className,
  onClick,
  ref,
  ...props
}: ComponentProps<'button'> & { ref?: Ref<HTMLButtonElement> }) => {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      type="button"
      ref={ref}
      data-sidebar="trigger"
      className={cn(
        'tw:inline-flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-md tw:text-sidebar-foreground tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-sidebar-ring',
        className
      )}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <svg
        className="tw:size-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M9 3v18" />
      </svg>
      <span className="tw:sr-only">Toggle Sidebar</span>
    </button>
  )
}

const SidebarRail = ({ className, ref, ...props }: ComponentProps<'button'> & { ref?: Ref<HTMLButtonElement> }) => {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'tw:absolute tw:inset-y-0 tw:z-20 tw:hidden tw:w-4 tw:-translate-x-1/2 tw:transition-all tw:ease-linear tw:after:absolute tw:after:inset-y-0 tw:after:left-1/2 tw:after:w-[2px] tw:hover:after:bg-sidebar-border tw:group-data-[side=left]:-right-4 tw:group-data-[side=right]:left-0 tw:sm:flex',
        'tw:in-data-[side=left]:cursor-w-resize tw:in-data-[side=right]:cursor-e-resize',
        'tw:[[data-side=left][data-state=collapsed]_&]:cursor-e-resize tw:[[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'tw:group-data-[collapsible=offcanvas]:translate-x-0 tw:group-data-[collapsible=offcanvas]:after:left-full tw:hover:group-data-[collapsible=offcanvas]:bg-sidebar',
        'tw:[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        'tw:[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className
      )}
      {...props}
    />
  )
}

const SidebarInset = ({ className, ref, ...props }: ComponentProps<'main'> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <main
      ref={ref}
      className={cn(
        'tw:relative tw:flex tw:w-full tw:flex-1 tw:flex-col tw:bg-white',
        'tw:md:peer-data-[variant=inset]:m-2 tw:md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 tw:md:peer-data-[variant=inset]:ml-0 tw:md:peer-data-[variant=inset]:rounded-xl tw:md:peer-data-[variant=inset]:shadow',
        className
      )}
      {...props}
    />
  )
}

const SidebarInput = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof Input> & { ref?: Ref<ComponentRef<typeof Input>> }) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'tw:h-8 tw:w-full tw:bg-white tw:shadow-none tw:focus-visible:ring-2 tw:focus-visible:ring-sidebar-ring',
        className
      )}
      {...props}
    />
  )
}

const SidebarHeader = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} data-sidebar="header" className={cn('tw:flex tw:flex-col tw:gap-2 tw:p-2', className)} {...props} />
  )
}

const SidebarFooter = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <div ref={ref} data-sidebar="footer" className={cn('tw:flex tw:flex-col tw:gap-2 tw:p-2', className)} {...props} />
  )
}

const SidebarSeparator = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof Separator> & { ref?: Ref<ComponentRef<typeof Separator>> }) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('tw:mx-2 tw:w-auto tw:bg-sidebar-border', className)}
      {...props}
    />
  )
}

const SidebarContent = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-2 tw:overflow-auto tw:group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  )
}

const SidebarGroup = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('tw:relative tw:flex tw:w-full tw:min-w-0 tw:flex-col tw:p-2', className)}
      {...props}
    />
  )
}

const SidebarGroupLabel = ({
  className,
  asChild = false,
  ref,
  ...props
}: ComponentProps<'div'> & { asChild?: boolean; ref?: Ref<HTMLDivElement> }) => {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        'tw:flex tw:h-8 tw:shrink-0 tw:items-center tw:rounded-md tw:px-2 tw:text-xs tw:font-medium tw:text-sidebar-foreground/70 tw:outline-hidden tw:ring-sidebar-ring tw:transition-[margin,opacity] tw:duration-200 tw:ease-linear tw:focus-visible:ring-2 tw:[&>svg]:size-4 tw:[&>svg]:shrink-0',
        'tw:group-data-[collapsible=icon]:-mt-8 tw:group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  )
}

const SidebarGroupAction = ({
  className,
  asChild = false,
  ref,
  ...props
}: ComponentProps<'button'> & { asChild?: boolean; ref?: Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        'tw:absolute tw:right-3 tw:top-3.5 tw:flex tw:aspect-square tw:w-5 tw:items-center tw:justify-center tw:rounded-md tw:p-0 tw:text-sidebar-foreground tw:outline-hidden tw:ring-sidebar-ring tw:transition-transform tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:focus-visible:ring-2 tw:[&>svg]:size-4 tw:[&>svg]:shrink-0',
        'tw:after:absolute tw:after:-inset-2 tw:lg:after:hidden',
        'tw:group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

const SidebarGroupContent = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => (
  <div ref={ref} data-sidebar="group-content" className={cn('tw:w-full tw:text-sm', className)} {...props} />
)

const SidebarMenu = ({ className, ref, ...props }: ComponentProps<'ul'> & { ref?: Ref<HTMLUListElement> }) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn('tw:flex tw:w-full tw:min-w-0 tw:flex-col tw:gap-1', className)}
    {...props}
  />
)

const SidebarMenuItem = ({ className, ref, ...props }: ComponentProps<'li'> & { ref?: Ref<HTMLLIElement> }) => (
  <li ref={ref} data-sidebar="menu-item" className={cn('tw:group/menu-item tw:relative', className)} {...props} />
)

const sidebarMenuButtonVariants = ({
  variant = 'default',
  size = 'default',
}: { variant?: 'default' | 'outline'; size?: 'default' | 'sm' | 'lg' } = {}) =>
  cn(
    'tw:peer/menu-button tw:flex tw:w-full tw:items-center tw:gap-2 tw:overflow-hidden tw:rounded-md tw:p-2 tw:text-left tw:text-sm tw:outline-hidden tw:ring-sidebar-ring tw:transition-[width,height,padding] tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:focus-visible:ring-2 tw:active:bg-sidebar-accent tw:active:text-sidebar-accent-foreground tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:group-has-data-[sidebar=menu-action]/menu-item:pr-8 tw:aria-disabled:pointer-events-none tw:aria-disabled:opacity-50 tw:data-[active=true]:bg-sidebar-accent tw:data-[active=true]:font-medium tw:data-[active=true]:text-sidebar-accent-foreground tw:data-[state=open]:hover:bg-sidebar-accent tw:data-[state=open]:hover:text-sidebar-accent-foreground tw:group-data-[collapsible=icon]:justify-center tw:[&>span:last-child]:truncate tw:[&>svg]:shrink-0',
    variant === 'outline' &&
      'tw:bg-white tw:shadow-[0_0_0_1px_var(--sidebar-border)] tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:hover:shadow-[0_0_0_1px_var(--sidebar-accent)]',
    size === 'sm' && 'tw:h-7 tw:text-xs',
    size === 'lg' && 'tw:h-12 tw:text-sm tw:group-data-[collapsible=icon]:p-0'
  )

const SidebarMenuButton = ({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ref,
  ...props
}: ComponentProps<'button'> & {
  asChild?: boolean
  isActive?: boolean
  tooltip?: string | ComponentProps<typeof TooltipContent>
} & { variant?: 'default' | 'outline'; size?: 'default' | 'sm' | 'lg' } & {
  ref?: Ref<HTMLButtonElement>
}) => {
  const Comp = asChild ? Slot : 'button'
  const { isMobile, state } = useSidebar()

  const button = (
    <Comp
      ref={ref}
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  )

  if (!tooltip) {
    return button
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={state !== 'collapsed' || isMobile} {...tooltip} />
    </Tooltip>
  )
}

const SidebarMenuAction = ({
  className,
  asChild = false,
  showOnHover = false,
  ref,
  ...props
}: ComponentProps<'button'> & {
  asChild?: boolean
  showOnHover?: boolean
  ref?: Ref<HTMLButtonElement>
}) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        'tw:absolute tw:right-1 tw:top-1.5 tw:flex tw:aspect-square tw:w-5 tw:items-center tw:justify-center tw:rounded-md tw:p-0 tw:text-sidebar-foreground tw:outline-hidden tw:ring-sidebar-ring tw:transition-transform tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:focus-visible:ring-2 tw:peer-hover/menu-button:text-sidebar-accent-foreground tw:[&>svg]:size-4 tw:[&>svg]:shrink-0',
        'tw:after:absolute tw:after:-inset-2 tw:lg:after:hidden',
        'tw:peer-data-[size=sm]/menu-button:top-1',
        'tw:peer-data-[size=default]/menu-button:top-1.5',
        'tw:peer-data-[size=lg]/menu-button:top-2.5',
        'tw:group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'tw:group-focus-within/menu-item:opacity-100 tw:group-hover/menu-item:opacity-100 tw:data-[state=open]:opacity-100 tw:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground tw:lg:opacity-0',
        className
      )}
      {...props}
    />
  )
}

const SidebarMenuBadge = ({ className, ref, ...props }: ComponentProps<'div'> & { ref?: Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      'tw:pointer-events-none tw:absolute tw:right-1 tw:flex tw:h-5 tw:min-w-5 tw:select-none tw:items-center tw:justify-center tw:rounded-md tw:px-1 tw:text-xs tw:font-medium tw:tabular-nums tw:text-sidebar-foreground',
      'tw:peer-hover/menu-button:text-sidebar-accent-foreground tw:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
      'tw:peer-data-[size=sm]/menu-button:top-1',
      'tw:peer-data-[size=default]/menu-button:top-1.5',
      'tw:peer-data-[size=lg]/menu-button:top-2.5',
      'tw:group-data-[collapsible=icon]:hidden',
      className
    )}
    {...props}
  />
)

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
